import { XMarkIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import React, { useState } from "react";
import { useEffect } from "react";
import Spinner from "../Spinner";
import { Button } from "../Button";
import { FormLabel } from "./FormLabel";

type InputSize = "lg" | "md" | "sm" | "xs";

type NativeInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "size"
>;

interface IconObject {
  left?: React.ReactNode;
  right?: React.ReactNode;
}

interface InputProps extends NativeInputProps {
  wrapperClasses?: string;
  size?: InputSize;
  labelClasses?: string;
  label?: React.ReactNode;
  icon?: React.ReactElement | IconObject;
  loading?: boolean;
  clearable?: boolean;
  footer?: React.ReactNode;
  footerButton?: React.ReactNode;
  footerButtonClick?: React.MouseEventHandler<HTMLButtonElement>;
  info?: React.ReactNode;
  error?: React.ReactNode;
  onChange?: (target: HTMLInputElement | string) => void;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      wrapperClasses,
      size,
      labelClasses,
      icon,
      loading,
      clearable,
      footer,
      footerButton,
      footerButtonClick,
      info,
      error,
      onChange: onChangeProp,
      ...rest
    },
    ref
  ) => {
    const {
      value: valueProp,
      className,
      id,
      type,
      placeholder,
      autoComplete,
      onBlur,
      required,
      ...inputProps
    } = rest;

    const [value, setValue] = useState<string>(
      String(valueProp ?? "") as string
    );
    const [showError, setShowError] = useState(false);
    const isIconReactElement = React.isValidElement(icon);

    useEffect(() => {
      if (valueProp !== undefined && valueProp !== null) {
        setValue(String(valueProp));
      } else {
        setValue("");
      }
    }, [valueProp]);

    const sizeLg = size === "lg";
    const sizeMd = size === "md" || !size;
    const sizeSm = size === "sm";
    const sizeXs = size === "xs";

    const wrapClasses = classNames("block", "text-left", wrapperClasses);

    const labelClassNames = classNames(
      "block mb-1",
      "text-sm font-medium",
      "text-left",
      labelClasses,
      {
        "text-gray-900": !showError,
        "text-red-700": showError,
      }
    );

    const classes = classNames(
      "shadow-sm bg-white",
      // "focus-visible:ring-1 focus:ring-1",
      "block w-full focus:outline-none focus-visible:ring-primary-500",
      "border rounded-lg bg-transparent",
      { "p-4 text-md": sizeLg },
      { "p-2.5 text-sm": sizeMd },
      { "p-2 text-xs": sizeSm },
      { "p-1 text-xs": sizeXs },
      {
        "border-gray-300 focus:ring-primary-500 focus:border-primary-500 text-gray-900":
          !showError,
      },
      {
        "bg-red-50 border-red-500 text-red-900 placeholder-red-700 focus:outline-2 focus:ring-red-500 focus:border-red-500 focus-visible:ring-red-500 focus-visible:border-red-500":
          showError,
      },
      { "pl-8": !!(icon && !isIconReactElement && (icon as IconObject)?.left) },
      className
    );

    const iconClasses = classNames(
      "flex absolute inset-y-0 right-0 items-center px-3",
      {
        "mr-6": type === "number",
      }
    );

    function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
      if (error) {
        setShowError(true);
      } else {
        setShowError(false);
      }

      onBlur && onBlur(e);
    }

    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
      if (!error) setShowError(false);
      onChangeProp && onChangeProp(e.target);
      setValue(e.target.value);
    }

    function clearValue() {
      onChangeProp && onChangeProp("");
      setValue("");
    }

    return (
      <div className={wrapClasses}>
        {rest.label && (
          <FormLabel
            htmlFor={id}
            className={labelClassNames}
            required={required}
          >
            {rest.label}
          </FormLabel>
        )}
        <div className="relative w-full">
          {((icon && !isIconReactElement) || loading) && (
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              {!isIconReactElement && (icon as IconObject)?.left}
              {loading && <Spinner size="md" className="ml-2" />}
            </div>
          )}
          <input
            ref={ref}
            {...inputProps}
            onChange={handleChange}
            type={type || "text"}
            id={id}
            value={loading ? undefined : value}
            className={classes}
            placeholder={loading ? undefined : placeholder}
            onBlur={handleBlur}
            autoComplete={autoComplete}
            data-loading={loading ? "true" : undefined}
            data-clearable={clearable ? "true" : undefined}
            required={required}
          />
          {icon && (isIconReactElement || (icon as IconObject)?.right) && (
            <div className={iconClasses}>
              {!isIconReactElement && (icon as IconObject)?.right}
              {isIconReactElement && icon}
            </div>
          )}
          {clearable && (
            <button
              type="button"
              className="flex absolute inset-y-0 right-0 items-center pr-3"
              onClick={clearValue}
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
        </div>
        {footer ? (
          footer
        ) : footerButton ? (
          <div className="flex justify-end">
            <Button type="link" onClick={footerButtonClick}>
              {footerButton}
            </Button>
          </div>
        ) : (
          info && (
            <p
              id="helper-text-explanation"
              className="mt-2 text-sm text-gray-500"
            >
              {info}
            </p>
          )
        )}

        {error && showError && (
          <p className="mt-2 text-sm text-red-600">{error}</p>
        )}
      </div>
    );
  }
);

export default Input;
